import React, { useContext, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Edit2,
  Trash2,
  Search,
  User,
  X,
  Check,
  AlertCircle,
  TrendingUp,
  CreditCard,
  Calendar,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { ImSad } from "react-icons/im";
import DataContext from "../Context/DAtaContext";
import UsersContext from "../Context/UsersContext";
import supabase from "../Config/Supabase";

function ManagePayments() {
  const { Payment, fetchPayments } = useContext(DataContext);
  const { farmers } = useContext(UsersContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingPayment, setEditingPayment] = useState(null);
  const [editForm, setEditForm] = useState({ amount: "", payment_method: "" });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [expandedFarmer, setExpandedFarmer] = useState(null);

  const paymentDetails = farmers
    ?.map((farmer) => {
      const payment = Payment?.filter((p) => p.farmer_id === farmer.uuid);
      return { farmer, payment };
    })
    .filter((item) => item.payment && item.payment.length > 0)
    .filter(
      (item) =>
        item.farmer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.farmer.id?.toString().includes(searchTerm),
    );

  const calculateTotal = (payments) =>
    payments.reduce((total, payment) => total + payment.amount, 0);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-TZ", {
      style: "currency",
      currency: "TZS",
      maximumFractionDigits: 0,
    }).format(amount);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const handleEditPayment = (payment) => {
    setEditingPayment(payment.id);
    setEditForm({
      amount: payment.amount.toString(),
      payment_method: payment.payment_method,
    });
  };

  const handleSaveEdit = async (paymentId) => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("payments")
        .update({
          amount: parseFloat(editForm.amount),
          payment_method: editForm.payment_method,
          updated_at: new Date().toISOString(),
        })
        .eq("id", paymentId);

      if (error) throw error;
      setNotification({
        type: "success",
        message: "Payment updated successfully!",
      });
      fetchPayments();
      setEditingPayment(null);
      setEditForm({ amount: "", payment_method: "" });
    } catch (error) {
      setNotification({ type: "error", message: "Failed to update payment" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("payments")
        .delete()
        .eq("id", paymentId);

      if (error) throw error;
      setNotification({
        type: "success",
        message: "Payment deleted successfully!",
      });
      fetchPayments();
      setDeleteConfirm(null);
    } catch (error) {
      setNotification({ type: "error", message: "Failed to delete payment" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const totalAllPayments =
    paymentDetails?.reduce(
      (sum, item) => sum + calculateTotal(item.payment),
      0,
    ) ?? 0;

  return (
    <div className="min-h-screen text-gray-900 dark:bg-inherit dark:text-white">
      <div className="py-8 mx-auto sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="mb-8"
        >
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div className="">
              <p className="mb-1 text-xs font-semibold tracking-widest uppercase text-emerald-500 dark:text-emerald-400">
                Finance
              </p>
              <h1 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white">
                Manage Investments
              </h1>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Track, edit and manage all farmer payment records
              </p>
            </div>

            {/* Summary pill */}
            {paymentDetails?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="items-center hidden gap-3 px-5 py-3 bg-white border border-gray-200 shadow-sm md:flex dark:bg-gray-900 dark:border-gray-800 rounded-2xl"
              >
                <div className="p-2 bg-gray-200 rounded-xl dark:bg-gray-800">
                  <TrendingUp className="w-4 h-4 text-green-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-400">
                    Total Portfolio
                  </p>
                  <p className="text-lg font-bold leading-tight text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(totalAllPayments)}
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="mb-6"
        >
          <div className="relative max-w-sm">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search farmers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 md:py-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 dark:focus:border-emerald-600 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute text-gray-400 -translate-y-1/2 right-3 top-1/2 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </motion.div>

        {/* Notification Toast */}
        <AnimatePresence>
          {notification && (
            <motion.div
              initial={{ opacity: 0, y: -60, x: "50%" }}
              animate={{ opacity: 1, y: 0, x: "50%" }}
              exit={{ opacity: 0, y: -60, x: "50%" }}
              className={`fixed top-5 right-1/2 z-50 flex items-center gap-2.5 px-5 py-3 rounded-xl shadow-xl text-sm font-medium text-white backdrop-blur-sm ${
                notification.type === "success"
                  ? "bg-emerald-500/90"
                  : "bg-red-500/90"
              }`}
            >
              {notification.type === "success" ? (
                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-white/20">
                  <Check className="w-3 h-3" />
                </div>
              ) : (
                <AlertCircle className="w-4 h-4" />
              )}
              {notification.message}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Payment Cards */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.07 } } }}
          className="space-y-1"
        >
          {paymentDetails?.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center py-20 bg-white border border-gray-200 dark:bg-gray-900 rounded-2xl dark:border-gray-800"
            >
              <div className="p-5 mb-4 bg-gray-100 rounded-full dark:bg-gray-800">
                <ImSad className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-1 text-base font-semibold">
                No payments found
              </h3>
              <p className="max-w-xs text-sm text-center text-gray-400">
                {searchTerm
                  ? "Try a different name or ID to find what you're looking for"
                  : "No farmers have made payments yet"}
              </p>
            </motion.div>
          ) : (
            paymentDetails?.slice(0, 10)?.map((item) => {
              const isExpanded = expandedFarmer === item.farmer.uuid;
              const total = calculateTotal(item.payment);

              return (
                <motion.div
                  key={item.farmer.uuid}
                  variants={{
                    hidden: { opacity: 0, y: 16 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.35, ease: "easeOut" },
                    },
                  }}
                  className="overflow-hidden bg-white border border-gray-200 shadow-sm dark:bg-gray-900 rounded-2xl dark:border-gray-800"
                >
                  {/* Farmer Header — clickable to expand */}
                  <button
                    onClick={() =>
                      setExpandedFarmer(isExpanded ? null : item.farmer.uuid)
                    }
                    className="w-full px-5 py-4 text-left transition-colors sm:px-6 sm:py-5 hover:bg-gray-50 dark:hover:bg-gray-800/60 group"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        {/* Avatar */}
                        <div className="relative shrink-0">
                          <div className="flex items-center justify-center shadow-md w-11 h-11 rounded-2xl bg-gradient-to-br from-green-600 to-emerald-600">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <span className="absolute w-4 h-4 border-2 border-white rounded-full -bottom-1 -right-1 bg-emerald-500 dark:border-gray-900" />
                        </div>

                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                            {item.farmer.name}
                          </h3>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-400">
                              ID-{item.farmer.id}
                            </span>
                            <span className="hidden w-1 h-1 bg-gray-300 rounded-full xl:block dark:bg-gray-700" />
                            <span className="hidden text-xs text-gray-400 capitalize xl:block">
                              {item.farmer.prefered_bundle}
                            </span>
                            <span className="hidden w-1 h-1 bg-gray-300 rounded-full xl:block dark:bg-gray-700" />
                            <span className="hidden text-xs text-gray-400 md:block">
                              {item.payment.length} payment
                              {item.payment.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="hidden text-right sm:block">
                          <p className="text-xs text-gray-400 mb-0.5">Total</p>
                          <p className="text-base font-bold text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(total)}
                          </p>
                        </div>
                        <motion.div
                          animate={{ rotate: isExpanded ? 90 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                        </motion.div>
                      </div>
                    </div>

                    {/* Mobile total */}
                    <div className="flex items-center justify-between mt-3 sm:hidden">
                      <span className="text-xs text-gray-400">
                        Total Invested
                      </span>
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(total)}
                      </span>
                    </div>
                  </button>

                  {/* Payment rows */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 py-4 space-y-3 border-t border-gray-100 dark:border-gray-800 sm:px-6">
                          {item?.payment?.map((payment, idx) => (
                            <motion.div
                              key={payment.id}
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.06 }}
                              className="flex flex-col gap-4 py-4 px-2 transition-colors border border-gray-100 group sm:flex-row sm:items-center rounded-xl bg-gray-50 dark:bg-gray-800/60 dark:border-gray-700/60 hover:border-gray-200 dark:hover:border-gray-600"
                            >
                              {/* Left: data or edit fields */}
                              <div className="flex-1 min-w-0">
                                {editingPayment === payment.id ? (
                                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-1 xl:grid-cols-2">
                                    <div>
                                      <label className="block mb-1 text-xs text-gray-400">
                                        Amount (TZS)
                                      </label>
                                      <input
                                        type="number"
                                        value={editForm.amount}
                                        onChange={(e) =>
                                          setEditForm({
                                            ...editForm,
                                            amount: e.target.value,
                                          })
                                        }
                                        className="w-full px-3 py-2 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                                        placeholder="Amount"
                                      />
                                    </div>
                                    <div className="hidden xl:block">
                                      <label className="block mb-1 text-xs text-gray-400">
                                        Payment Method
                                      </label>
                                      <input
                                        type="text"
                                        value={editForm.payment_method}
                                        onChange={(e) =>
                                          setEditForm({
                                            ...editForm,
                                            payment_method: e.target.value,
                                          })
                                        }
                                        className="w-full px-3 py-2 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                                        placeholder="e.g. M-Pesa"
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-2 md:grid-cols-1 gap-2 items-center xl:grid-cols-3 sm:gap-4">
                                    <div className="flex  items-start gap-2">
                                      <div className="p-2.5 rounded-lg bg-white dark:bg-blue-950 mt-0.5 shrink-0">
                                        <TrendingUp className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                      </div>
                                      <div className="min-w-0">
                                        <p className="text-xs dark:text-gray-400 text-gray-600 mb-0.5">
                                          Amount
                                        </p>
                                        <p className="text-sm font-semibold  truncate dark:text-green-400">
                                          {formatCurrency(payment.amount)}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="items-start hidden gap-2 xl:flex">
                                      <div className="p-2.5 rounded-lg bg-white dark:bg-blue-950 mt-0.5 shrink-0">
                                        <CreditCard className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                      </div>
                                      <div className="min-w-0 ">
                                        <p className="text-xs dark:text-gray-400 text-gray-600 mb-0.5">
                                          Method
                                        </p>
                                        <p className="text-sm font-medium truncate">
                                          {payment.payment_method}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="items-start hidden gap-2 xl:flex">
                                      <div className="p-2.5 rounded-lg bg-white dark:bg-blue-950 mt-0.5 shrink-0">
                                        <Calendar className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                                      </div>
                                      <div className="min-w-0">
                                        <p className="text-xs dark:text-gray-400 text-gray-600 mb-0.5">
                                          Date
                                        </p>
                                        <p className="text-xs font-medium truncate">
                                          {formatDate(payment.created_at)}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex md:hidden items-end justify-end gap-1.5 shrink-0">
                                      {editingPayment === payment.id ? (
                                        <></>
                                      ) : (
                                        <>
                                          <button
                                            onClick={() =>
                                              handleEditPayment(payment)
                                            }
                                            className="p-2 text-blue-600 dark:text-blue-600 transition-colors rounded-lg hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 dark:hover:text-blue-400"
                                            title="Edit"
                                          >
                                            <Edit2 className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={() =>
                                              setDeleteConfirm(payment.id)
                                            }
                                            className="p-2 text-red-600 dark:text-red-600 transition-colors rounded-lg hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 dark:hover:text-red-400"
                                            title="Delete"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* action buttons for mobile view */}
                              <div className="flex md:hidden items-center justify-between gap-1.5 shrink-0">
                                {editingPayment === payment.id ? (
                                  <>
                                    <button
                                      onClick={() => handleSaveEdit(payment.id)}
                                      disabled={isLoading}
                                      className="flex items-center justify-center w-full gap-1.5 px-3.5 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-medium transition-colors disabled:opacity-50"
                                    >
                                      <Check className="w-3.5 h-3.5" />
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingPayment(null)}
                                      className="flex items-center justify-center gap-1.5 w-full px-3.5 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 text-xs font-medium transition-colors"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <></>
                                )}
                              </div>

                              {/* Action buttons */}
                              <div
                                className={`md:flex hidden ${editingPayment === payment.id ? "mt-5" : ""} items-center gap-1.5 shrink-0`}
                              >
                                {editingPayment === payment.id ? (
                                  <>
                                    <button
                                      onClick={() => handleSaveEdit(payment.id)}
                                      disabled={isLoading}
                                      className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg bg-green-600 hover:bg-green-600 text-white text-xs font-medium transition-colors disabled:opacity-50"
                                    >
                                      <Check className="w-3.5 h-3.5" />
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingPayment(null)}
                                      className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 text-xs font-medium transition-colors"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleEditPayment(payment)}
                                      className="p-2  text-blue-600 dark:text-blue-600 transition-colors rounded-lg hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 dark:hover:text-blue-400"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-4 h-4 " />
                                    </button>
                                    <button
                                      onClick={() =>
                                        setDeleteConfirm(payment.id)
                                      }
                                      className="p-2 text-red-600 transition-colors rounded-lg dark:text-red-600 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 dark:hover:text-red-400"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })
          )}
        </motion.div>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 12 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm p-6 bg-white border border-gray-200 shadow-2xl dark:bg-gray-900 rounded-2xl dark:border-gray-800"
            >
              <div className="flex flex-col gap-4 mb-5">
                <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full dark:bg-gray-800 bg-red-50">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>

                <div className="text-center">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    Delete Payment
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    This action is permanent and cannot be undone. The payment
                    record will be removed.
                  </p>
                </div>
              </div>

              <div className="flex gap-2.5">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeletePayment(deleteConfirm)}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          repeat: Infinity,
                          duration: 0.8,
                          ease: "linear",
                        }}
                        className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full"
                      />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ManagePayments;
